import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { ProductCardSelectColor } from '@/components/blocks/ProductCardSelectColor';
import { RenderProductImg } from '@/components/blocks/RenderProductImg';
import { ProductRibbons } from '@/components/blocks/Ribbons';
import { ProductType, Ribbon } from '@/data/types/Product';
import { mergeRibbonsForProduct } from '@/utils/mergeRibbonsForProduct';
import { Box, Skeleton, Typography } from '@mui/material';
import { FC, ReactNode } from 'react';
import {
	cardSX,
	categorySX,
	discountSX,
	DpModernProductCardVariant,
	imageFrameSX,
	imageSX,
	infoSX,
	linkableSX,
	priceNoteSX,
	priceRowSX,
	priceSX,
	titleSX,
	VARIANTS,
} from '@/components/blocks//DpModernProductCard/styles';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { generateProductViewSeoHref } from '@/utils/generateProductViewSeoHref';

export type { DpModernProductCardVariant };

export const DpModernProductCard: FC<{
	variant?: DpModernProductCardVariant;
	title: string;
	category: string;
	image: string;
	price: string;
	discount?: string;
	priceNote?: ReactNode;
	seo?: string;
	showPrice?: boolean;
	ribbons?: Ribbon[];
	variants?: ProductType[];
	partNumber?: string;
	onSelectColor?: (variant: ProductType) => void;
	clickSource?: CLICK_PRODUCT_VIEW_SOURCE;
	onClick?: () => void;
}> = ({
	variant = 'mini',
	title,
	category,
	image,
	price,
	discount,
	priceNote,
	seo,
	showPrice,
	ribbons = [],
	variants = [],
	partNumber,
	onSelectColor,
	clickSource,
	onClick,
}) => {
	const t = VARIANTS[variant];
	const sortedRibbons = t.showRibbons ? mergeRibbonsForProduct(ribbons) : [];
	const hasDiscount = Boolean(discount);

	return (
		<Box className="product-card-container" sx={cardSX(t)}>
			<Linkable
				className="product-card-linkable"
				href={generateProductViewSeoHref(seo, clickSource)}
				draggable={false}
				onClick={onClick}
				sx={linkableSX}
			>
				<Box className="product-card-frame" sx={imageFrameSX(t)}>
					<RenderProductImg
						fullImageRaw={image}
						render={(img) => (
							<Img
								className="product-card-image"
								src={img}
								alt={title}
								draggable={false}
								sx={imageSX(t)}
							/>
						)}
					/>
					{t.showRibbons ? <ProductRibbons ribbons={sortedRibbons} /> : null}
				</Box>
				<Box sx={infoSX(t)}>
					<ProductCardSelectColor
						variants={variants}
						selectedPartNumber={partNumber ?? ''}
						sx={{ mb: '6px' }}
						onSelectColor={onSelectColor}
					/>
					<Typography sx={titleSX(t)}>{title}</Typography>
					<Typography sx={categorySX(t)}>{category}</Typography>
					<Box sx={priceRowSX(t)}>
						{hasDiscount && showPrice ? (
							<Typography sx={discountSX(t)}>{discount}</Typography>
						) : null}
						<Typography sx={priceSX(t, hasDiscount)}>
							{showPrice ? (
								price
							) : (
								<Skeleton variant="rectangular" width={t.skeletonWidth} height={t.skeletonHeight} />
							)}
						</Typography>
					</Box>
					{priceNote && showPrice ? <Box sx={priceNoteSX}>{priceNote}</Box> : null}
				</Box>
			</Linkable>
		</Box>
	);
};
